import React, { FC, useMemo } from "react";

import { Stack } from "react-bootstrap";

import { Participant } from "@api/graphql";
import { isCollaboration, isParticipantAmpOrArtCentreOrCMO, isParticipantArtistOrBeneficiary } from "@util/index";

import styles from "./ParticipantDropdown.module.scss";

interface ParticipantDropdownProps {
  filteredResults: Participant[];
  onParticipantSelect: (participant: ParticipantWithSinglePseudonym) => void;
  visible: boolean;
}

export interface ParticipantWithSinglePseudonym extends Omit<Participant, "pseudonym"> {
  pseudonym: string;
}

interface ParticipantCardProps {
  participant: ParticipantWithSinglePseudonym;
  onParticipantSelect: (participant: ParticipantWithSinglePseudonym) => void;
}

const ParticipantCard: FC<ParticipantCardProps> = ({ participant, onParticipantSelect }) => {
  const isUserArtistOrBeneficiary = isParticipantArtistOrBeneficiary(participant);
  const isUserAmpOrArtCentreOrCMO =
    isParticipantAmpOrArtCentreOrCMO(participant) || isCollaboration(participant.participant_types);
  return (
    <Stack
      key={participant.participant_id}
      className={styles.dropdownItem}
      onClick={() => onParticipantSelect(participant)}
      direction="vertical"
    >
      <Stack direction="horizontal">
        <span className={styles.propTitle}>ID: </span>
        <span>{participant.participant_id}</span>
      </Stack>
      {/* if a user is an artist or a beneficiary then display full name */}
      {isUserArtistOrBeneficiary && !isUserAmpOrArtCentreOrCMO && (
        <Stack direction="horizontal">
          <span className={styles.propTitle}>Name: </span>
          <span>
            {participant.full_name}
            {participant.participant_status === "INACTIVE" ? ` (${participant.participant_status})` : ""}
          </span>
        </Stack>
      )}
      {/* if a user is an AMP or ART CENTRE then display company name */}
      {isUserAmpOrArtCentreOrCMO && (
        <Stack direction="horizontal">
          <span className={styles.propTitle}>Company Name: </span>
          <span>
            {participant.company_name || "-"}
            {participant.participant_status === "INACTIVE" ? ` (${participant.participant_status})` : ""}
          </span>
        </Stack>
      )}
      <Stack direction="horizontal">
        <span className={styles.propTitle}>Pseudonym: </span>
        <span>{participant.pseudonym || "-"}</span>
      </Stack>
    </Stack>
  );
};

const ParticipantDropdown: React.FC<ParticipantDropdownProps> = ({ filteredResults, onParticipantSelect, visible }) => {
  const resultsToDisplay = useMemo(() => {
    return filteredResults.reduce((acc, item) => {
      const pseudonyms = Array.isArray(item.pseudonym) && item.pseudonym.length > 0 ? item.pseudonym : undefined;
      if (!pseudonyms) {
        return [...acc, { ...item, pseudonym: "" }];
      } else {
        return [...acc, ...pseudonyms.map((pseudonym) => ({ ...item, pseudonym }))];
      }
    }, [] as ParticipantWithSinglePseudonym[]);
  }, [filteredResults]);

  if (!visible) {
    return null;
  }

  return (
    <div className={`${styles.dropdownList} ${visible ? styles.visible : ""}`}>
      {resultsToDisplay.map((item) => (
        <ParticipantCard
          key={`${item.participant_id}-${item.pseudonym}`}
          participant={item}
          onParticipantSelect={onParticipantSelect}
        />
      ))}
    </div>
  );
};

export default ParticipantDropdown;
